/**
 * Fetch the active fuel-plan policy for surfaces that have NO plan in scope
 * (Activities, future standalone screens). Lives outside `useFuelPlan` /
 * `usePlannedWorkouts` so it can be consumed independently — each surface
 * pays one cached request, not one per workout.
 *
 * Plan views should read `plan.policySnapshot` instead (that's frozen at
 * dispatch); the planner reads policy via `usePlannedWorkouts` (shipped on
 * the range response). This hook is for everything else.
 *
 * Caching: relies on the route's `Cache-Control: public, max-age=60` for
 * cross-tab freshness; in-memory state-only here.
 */

import { useEffect, useState } from "react";
import { apiCall } from "../lib/api";
import type { FuelPolicySnapshot } from "../lib/fuelPlanTypes";

type Response = {
  policy: FuelPolicySnapshot;
  policyRevisionId: string | null;
  policySource: string;
};

export function useActivePolicy(): FuelPolicySnapshot | null {
  const [policy, setPolicy] = useState<FuelPolicySnapshot | null>(null);
  useEffect(() => {
    let cancelled = false;
    apiCall<Response>("/api/fuel-plans/policy/active")
      .then((res) => {
        if (!cancelled) setPolicy(res.policy);
      })
      .catch(() => {
        // Best-effort — consumers always fall back to bundled FUEL_TIERS
        // when policy is null. No need to surface the error.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return policy;
}
